import { useState, useEffect } from "react";
import Select from "react-select";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  ButtonGroup,
  Button,
  Image
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";
import { ThreeDots } from 'react-loader-spinner';
import {
  getLeaveBalances,
  updateApplication,
  updateBalance,
  postLeaveApplication,
  getDaysApplying,
  resetForm,
  getLeaveDetailsByID,
  updateReason,
  withdrawLeaveApplication,
  getLeaveApplicationDetails,
  deleteLeaveApplication,
  getMedicalCert,
  addMedicalCertificate,
  editMedicalCertificate,
  updateApplicationDetails,
  editLeaveApplication,
  updateCert,
  addSpecialAttachment,
  getSpecialAttachment,
  editSpecialAttachment,
  LeaveApplicationOverlap
} from "../../3.Store/ApplicationSlice";
import { getListsOfLeaveWithdrawal } from "../../3.Store/LeaveWithdrawalSlice";
import { openEditor } from "react-profile";
import "react-profile/themes/default";
import PDFViewer from "../../2.Commons/PDFViewer";
import DatePicker from "react-datepicker";
import { current } from "@reduxjs/toolkit";

const LeaveApplicationForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {id} = useParams();
  // const [loading, setLoading] = useState(false);
  const [fullImage, setFullImage] = useState(null);
  const loading = useSelector(state => state.ApplicationSlice.loading);
  const withdrawalList = useSelector(state => state.LeaveWithdrawalSlice.lists);
  const [originalDaysApplied, setOriginalDaysApplied] = useState(0);

  const employee = JSON.parse(localStorage.getItem("current_user"));
  const userId = employee?.id;

  const [showBalance, setShowBalance] = useState("");
  const [isError, setIsError] = useState(false);
  const [isReasonEmpty, setReasonEmpty] = useState(false);

  const { application, balance, applying, reason, cert, applicationDetails } = useSelector(
    (state) => state.ApplicationSlice
  );

  const {
    created,
    leave_type,
    location_type,
    reason_for_application,
    number_of_days,
    numberofday_type,
    from_date,
    from_ampm,
    to_date,
    to_ampm,
    description,
    status,
  } = application;
  const { reason_withdrawal } = reason;

  const [originalLeaveTypeId, setOriginalLeaveTypeId] = useState(null);

  console.log(originalLeaveTypeId);

  const leaveData = JSON.parse(localStorage.getItem("leaveData"));
  const date_joined = moment(leaveData?.dateJoined).format("YYYY-MM-DD");
  const isOneYearOrMore = moment().diff(moment(date_joined), "years") >= 1;

  useEffect(() => {
    window.scrollTo(0, 0);
  
    // Always get the current balance
    dispatch(getLeaveBalances({ id: userId }));
  
    const loadData = async () => {
      if (id) {
        // Fetch the leave details first
        await dispatch(getLeaveDetailsByID(id));
  
        try {
          // unwrap() waits for async thunk and gives real data
          const details = await dispatch(getLeaveApplicationDetails({ id })).unwrap();
  
          if (details) {
            // Save original info for balance calculation
            setOriginalLeaveTypeId(details.leave_type?.id ?? null);
            setOriginalDaysApplied(Number(details.number_of_days ?? 0));
  
            console.log("Fetched leave details:", details);
          }
        } catch (err) {
          console.error("Failed to fetch leave details:", err);
        }
      } else {
        dispatch(resetForm());
        setOriginalLeaveTypeId(null);
        setOriginalDaysApplied(0);
      }
    };
  
    loadData();
  }, [id, userId]);  

  useEffect(() => {
    // Function to fetch data based on leave type
    const fetchData = async () => {
      try {
        if (leave_type?.id === "SI" && id) {
          await dispatch(getMedicalCert({ id }));
        } else if (leave_type?.id === "SP" && id) {
          await dispatch(getSpecialAttachment({ id }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();

  }, [leave_type?.id]);

  useEffect(() => {
    if (status?.description.includes("Withdrawn") && status.description.includes("Withdrawn")) {
      console.log("Status contains 'withdraw'. Perform specific actions here.");
      dispatch(getListsOfLeaveWithdrawal());

      if (withdrawalList && withdrawalList.length > 0) {
        const matchedWithdrawal = withdrawalList.find(
          (withdrawal) => withdrawal.leave_application.id === application.id
        );
        console.log(matchedWithdrawal)
        dispatch(updateApplicationDetails({ ...matchedWithdrawal }));
      }
    }
  }, [status])

  useEffect(() => {
    updateBalance(balance[leave_type]);
    showBalanceAfter();
  }, [balance, leave_type]);

  // Calculate No. Days Applying on first load (the form opens with default
  // dates, so the field must not stay empty until the user touches a date).
  useEffect(() => {
    if (!leave_type?.id || !from_date?.date || !to_date?.date) return;
    if (number_of_days !== "" && number_of_days !== null && number_of_days !== undefined) return;

    handleMCase(from_date, to_date, from_ampm, to_ampm);
  }, [
    id,
    leave_type?.id,
    from_date?.date,
    to_date?.date,
    from_ampm,
    to_ampm,
    number_of_days,
  ]);

  // The number of days is missing (not calculated yet).
  const daysApplied = parseFloat(number_of_days);
  const isNumberOfDaysEmpty =
    number_of_days === "" ||
    number_of_days === null ||
    number_of_days === undefined ||
    isNaN(daysApplied);

  // Blocks submission when the number of days is missing or not positive.
  const hasInvalidNumberOfDays = () => {
    const days = daysApplied;

    if (isNumberOfDaysEmpty) {
      Swal.fire({
        title: "No. Days Applying Not Available",
        text: "The number of days applying is not calculated yet. Please re-select the dates. / Bilangan hari belum dikira. Sila pilih semula tarikh.",
        icon: "warning",
        confirmButtonText: "OK"
      });
      return true;
    }

    if (days <= 0) {
      Swal.fire({
        title: "Check Selected Date",
        text: "The selected date falls on a weekend or holiday. There is no need to apply for leave.",
        icon: "warning",
        confirmButtonText: "OK"
      });
      return true;
    }

    return false;
  };

  const appendTime = (date, ampm) => {
    return ampm === "AM" ? `${date} 08:00:00` : `${date} 13:00:00`;
  };
  
  const handleMCase = async (from_date, to_date, from_ampm, to_ampm) => {
    setIsError(false);
  
    const fromDateString = appendTime(moment(from_date.date).format("YYYY-MM-DD"), from_ampm);
    const toDateString = appendTime(moment(to_date.date).format("YYYY-MM-DD"), to_ampm);
  
    const entity = {
      ...applying,
      employee: { id: userId },
      from_datetime: fromDateString,
      leave_type: { id: leave_type.id },
      to_datetime: toDateString
    };
  
    try {
      const updatedDaysApply = await dispatch(getDaysApplying(entity));
  
      const days_applying = { ...application, 
        number_of_days: updatedDaysApply.payload,
        from_ampm: from_ampm,
        to_ampm: to_ampm,
        from_date: from_date,
        to_date: to_date,
        current_balance: showBalance
      };
      console.log(days_applying);
      await dispatch(updateApplication(days_applying));
  
      if (["SP", "A", "SI"].includes(leave_type?.id)) {
        let balanceAfterChange;
  
        if (!id) {
          balanceAfterChange = Number(showBalance) - Number(updatedDaysApply.payload);
        } else {
          if (showBalance !== 0 && originalDaysApplied !== 0) {
            balanceAfterChange =
              Number(showBalance) + Number(originalDaysApplied) - Number(number_of_days);
          }
        }
  
        if (balanceAfterChange < -5) {
          setIsError(true);
          Swal.fire({
            title: "Leave Balance Insufficient!",
            text: "You do not have enough leave balance.",
            icon: "warning",
            button: "OK",
          });
        }
      }
    } catch (error) {
      console.error("Error updating days applying:", error);
    }
  };  

  const LeaveTypeOption = [
    { id: "A", label: "Annual Leave", value: "annual" },
    // { id: "AD", label: "Advanced Leave", value: "advanced" },
    { id: "H", label: "Hospitalization", value: "hospitalization" },
    { id: "M", label: "Maternity Leave", value: "maternity" },
    { id: "P", label: "Paternity Leave", value: "paternity" },
    { id: "O", label: "Other Leave", value: "other" },
    { id: "SI", label: "Sick Leave", value: "sick" },
    { id: "SP", label: "Special Leave", value: "special" },
    { id: "UP", label: "Unpaid Leave", value: "unpaid" },
  ];

  const getLeaveOptions = (balance) => {
    let options = [...LeaveTypeOption];

    const leaveData = JSON.parse(localStorage.getItem("leaveData"));
    const gender = leaveData?.gender;

    if (gender === "M") {
      options = options.filter((option) => option.value !== "maternity");
    } 
    else if (gender === "F") {
      options = options.filter((option) => option.value !== "paternity");
    }

    // Add "Unpaid Leave" only if balance.annual is more than -4
    // if (Number(balance.annual) < -4) {
    //   options.push({ id: "UP", label: "Unpaid Leave", value: "unpaid" });
    // }

    // Add "Advanced Leave" only if balance.annual is = 0 (11/04/2026)
    // if (Number(balance.annual) <= 0) {
    //   options.push({ id: "AD", label: "Advanced Leave", value: "advanced" });
    // }

    return options;
  };

  const SpecialLeaveTypeOption = [
    {
      id: "1da74498-1637-11e9-9e53-5254006876cf",
      label: "Convocation (1 day)",
      value: "co",
      max_days: 1,
    },
    // {
    //   id: "287596e0-1637-11e9-9e53-5254006876cf",
    //   label: "Paternity Leave (2 days)",
    //   value: "PL",
    //   max_days: 2,
    // },
    {
      id: "093f0e92-1637-11e9-9e53-5254006876cf",
      label: "Demise of Immediate Family (3 days)",
      value: "DIF",
      max_days: 3,
    },
    {
      id: "0ddd720c-1637-11e9-9e53-5254006876cf",
      label: "Demise of Family (1 day)",
      value: "DF",
      max_days: 1,
    },
    {
      id: "185632cb-1637-11e9-9e53-5254006876cf",
      label: "Wedding (Max 2 days)",
      value: "W",
      max_days: 2,
    },
  ];

  const filteredSpecialLeaveOptions = SpecialLeaveTypeOption.filter(
    (opt) => opt.value !== "W" || isOneYearOrMore
  );  

  const NoOfDaysOption = [
    { id: "H", label: "1/2 Day", value: "half" },
    { id: "1", label: "One Day", value: "one" },
    { id: "M", label: "More than 1 Day", value: "more_than_one" },
  ];

  const LocationOption = [
    { id: "I", label: "In Town", value: "in_town" },
    { id: "OT", label: "Out of Town", value: "out_town" },
    { id: "W", label: "West Malaysia", value: "w_msia" },
    { id: "OS", label: "Oversea", value: "oversea" },
  ];

  const showBalanceAfter = () => {
    if (leave_type.id == "A") {
      setShowBalance(balance?.annual);
    }
    if (leave_type.id == "AD") {
      setShowBalance(balance?.advanced);
    }
    if (leave_type.id == "H") {
      setShowBalance(balance?.hospitalization);
    }
    if (leave_type.id == "M") {
      setShowBalance(balance?.maternity);
    }
    if (leave_type.id == "P") {
      setShowBalance(balance?.paternity);
    }
    if (leave_type.id == "SI") {
      setShowBalance(balance?.sick);
    }
    if (leave_type.id == "SP") {
      setShowBalance(balance?.special);
    }
    if (leave_type.id == "O") {
      setShowBalance(0);
    }
    if (leave_type.id == "UP") {
      setShowBalance(0);
    }
  };

  const handleChangeForm = (e) => {
    console.log(e.target.name, e.target.value);

    if (e.target.name === "reason_for_application") {
      const text = e.target.value;
    
      // Detect non-English characters
      const isNonEnglish = /[^\u0000-\u007F]/.test(text);
    
      if (isNonEnglish) {
        Swal.fire({
          text: "Only English is allowed. / Hanya Bahasa Inggeris dibenarkan.",
          icon: "warning",
          confirmButtonText: "OK",
        });
    
        // Restore previous valid value instead of clearing everything
        const entity = { ...application };
        console.log(entity);
    
        dispatch(updateApplication(entity));
    
        return; // stop further processing
      }
    }

    const entity = { ...application, [e.target.name]: e.target.value };
    console.log(entity);
    dispatch(updateApplication(entity));
    setReasonEmpty(false);

    // 👇 Only run when AM/PM fields change
    if (e.target.name === "from_ampm") {
      handleMCase(
        entity.from_date,
        entity.to_date,
        e.target.value,
        entity.to_ampm
      );
    }

    if (e.target.name === "to_ampm") {
      handleMCase(
        entity.from_date,
        entity.to_date,
        entity.from_ampm,
        e.target.value
      );
    }
  };    

  const handleChangeDropdownLeaveType = (value) => {
    if(value.id==="SP") {
      const entity = {
        ...application,
        leave_type: {
          id: value.id,
        },
        special_leave_type: {
          id: "1da74498-1637-11e9-9e53-5254006876cf"
        }
      };
      console.log(entity);
      dispatch(updateApplication(entity));
    } else {
      const entity = {
        ...application,
        leave_type: {
          id: value.id,
        }
      };
      console.log(entity);
      dispatch(updateApplication(entity));
    }
  };

  const handleChangeDropdownSpecialLeaveType = (value) => {
    console.log(value);
    const entity = {
      ...application,
      special_leave_type: {
        id: value.id,
        // description: action.label,
      },
    };
    dispatch(updateApplication(entity));
    console.log(entity);
  };

  // --- Run handleMCase when user changes dates ---
  const handleChangeFromDate = (e) => {
    const newFromDate = { date: e.target.value };
    const oldToDate = application.to_date;
  
    let correctedToDate = oldToDate;
  
    // If old to_date is earlier than new from_date → auto-correct it
    if (!oldToDate || moment(oldToDate.date).isBefore(moment(newFromDate.date))) {
      correctedToDate = newFromDate;
    }
  
    const entity = { 
      ...application, 
      from_date: newFromDate, 
      to_date: correctedToDate 
    };
  
    dispatch(updateApplication(entity));
    setIsError(false);
  
    handleMCase(newFromDate, correctedToDate, from_ampm, to_ampm);
  };  

  const handleChangeToDate = (e) => {
    const newDate = { date: e.target.value };
    const entity = { ...application, to_date: newDate };
    dispatch(updateApplication(entity));
    setIsError(false);

    // Call handleMCase with the *latest values*
    handleMCase(application.from_date || newDate, newDate, from_ampm, to_ampm);
  };

  const handleChangeDropdownLocation = (action) => {
    const entity = {
      ...application,
      location_type: {
        id: action.id,
        // description: action.label,
      },
    };
    dispatch(updateApplication(entity));
  };

  const handleReasonWithdrawal = (e) => {
    const text = e.target.value;
  
    // Detect NON-English characters (anything outside ASCII)
    const isNonEnglish = /[^\u0000-\u007F]/.test(text);
  
    if (isNonEnglish) {
      Swal.fire({
        text: "Only English is allowed. / Hanya Bahasa Inggeris dibenarkan.",
        icon: "warning",
        confirmButtonText: "OK",
      });
  
      // DO NOT update — keep previous correct value
      const entity = { ...reason };  
      dispatch(updateReason(entity));
  
      return; // stop further processing
    }
  
    // Normal valid update
    const entity = { ...reason, reason_withdrawal: text };
    dispatch(updateReason(entity));
  };  

  const handleBtnWithdraw = () => {
    if (!reason_withdrawal.trim() || reason_withdrawal.trim().length < 3) {
      // Display an error message if the reason is empty
      Swal.fire({
        icon: 'warning',
        text: 'Please enter a reason for withdrawal with at least 3 characters. / Sila masukkan sebab dengan sekurang-kurangnya 3 huruf.',
      });
      return; 
    }
  
    Swal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(withdrawLeaveApplication({id:id, ...reason}))
          .unwrap()
          .then(() => {
            Swal.fire({
              title: "Request for Withdrawal is submitted (subjected to approval)",
              icon: "success",
              showConfirmButton: true,
            }).then((result) => {
              if (result.isConfirmed) {
                navigate("/LeaveApplicationList");
              }
            });
          })
          .catch((err) => {
            Swal.fire("Error", err.response.data, "error", "OK");
          });
      } else if (result.dismiss === Swal.DismissReason.cancel) {
          Swal.fire(
            'Cancelled',
            'The approval has been cancelled',
            'error'
          );
      }
    });
  }  

  const handleBtnBack = () => {
    dispatch(resetForm());
    navigate(-1);
  };

  const handleBtnDelete = () => {
    Swal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteLeaveApplication({ id }))
          .unwrap()
          .then(() => {
            Swal.fire('Deleted!', 'Leave application has been deleted.', 'success')
              .then(() => {
                navigate("/LeaveApplicationList");
              });
          })
          .catch((err) => {
            Swal.fire("Error", err.response.data, "error");
          });
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        Swal.fire('Cancelled', 'Cancel Delete Leave Application', 'error');
      }
    });
  };

  // Function to send log to Logstash
  // const sendLogToLogstash = async (logData) => {
  //   try {
  //     await fetch("http://192.168.1.173/leave3/apiv2/public/index.php/application/module/employees/add2", {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //       },
  //       body: JSON.stringify(logData),
  //     });
  //   } catch (err) {
  //     console.error("Failed to send log to Logstash:", err);
  //   }
  // };

  const handleBtnSubmit = () => {

    if (hasInvalidNumberOfDays()) {
      setIsError(true);
      return;
    }

    // check attachment based on leave type
    if (leave_type.id === "SI") {
      if (!cert || Object.keys(cert).length === 0) {
        Swal.fire({
          text: "Please attach a medical certificate. / Sila lampirkan sijil sakit.",
          icon: "warning",
          confirmButtonText: "OK"
        });
        return; // Stop submission
      }
    }

    // Only apply max day check for Special Leave (SP)
    if (leave_type?.id === "SP") {
      const selectedSpecialLeaveType = SpecialLeaveTypeOption.find(
        (item) => item.id === application?.special_leave_type?.id
      );

      const daysRequested = parseFloat(number_of_days);
      const maxDays = selectedSpecialLeaveType?.max_days;

      if (daysRequested > maxDays) {
        setIsError(true);
        Swal.fire({
          text: `You can only apply up to ${maxDays} day(s) for this special leave type. / Anda hanya boleh memohon ${maxDays} hari untuk jenis cuti ini.`,
          icon: "warning",
          confirmButtonText: "OK"
        });
        return;
      }

      // 🔹 2. Check for required proof document
      if (!cert || Object.keys(cert).length === 0) {
        Swal.fire({
          text: "Please attach a proof document. / Sila lampirkan dokumen sokongan.",
          icon: "warning",
          confirmButtonText: "OK",
        });
        return;
      }
    }

    if (
      !reason_for_application.trim() || 
      reason_for_application.trim().length < 3 || 
      !Object.values(application).includes(reason_for_application)
    ) {
      setReasonEmpty(true); // Highlight reason field
    
      Swal.fire({
        icon: 'warning',
        text: 'Please enter a reason with at least 3 characters. / Sila masukkan sebab dengan sekurang-kurangnya 3 huruf.',
      });
    
      return; // Stop further execution
    } else {
      setReasonEmpty(false);
    }    
    
    // Check leave balance
    let balanceAfterChange = Number(showBalance) - Number(number_of_days);

    // Threshold: A = -5, others = 0
    let minBalance = 0;

    if (leave_type?.id === "A") {
      console.log("Annual leave selected, min balance is -5");
      minBalance = -5;
    } else if (leave_type?.id === "O" || leave_type?.id === "UP") {
      minBalance = null; // 👈 no limit
    } else {
      minBalance = 0;
    }

    // if (leave_type?.id === "O" || leave_type?.id === "UP") {
    //   minBalance = null;
    // } else {
    //   minBalance = 0;
    // }

    // Only check balance if there is a limit
    if (minBalance !== null && balanceAfterChange < minBalance) {
      setIsError(true);
      console.log(showBalance, number_of_days, balanceAfterChange);
      Swal.fire({
        title: "Leave Balance Insufficient!",
        text: "You do not have enough leave balance. / Anda tidak mempunyai baki cuti yang cukup.",
        icon: "warning",
        button: "OK",
      });
      return;
    }

    try {
      let day_description;
      let numberofday_type;
    
      const isSameDay = moment(from_date.date).isSame(moment(to_date.date), "day");

      if ((number_of_days !== "1.0" && number_of_days !== "0.5") || (number_of_days === "1.0" && !isSameDay)) {
        // Also covers 1.0 day spanning two dates, e.g. today PM + tomorrow AM
        day_description = `${number_of_days} days leave applied on ${moment(created).format("YYYY-MM-DD")}, from ${from_date.date} ${from_ampm} to ${to_date.date} ${to_ampm}.`;
        numberofday_type = "M";
      } else if (number_of_days === "0.5") {
        day_description = `${number_of_days} day leave applied on ${moment(created).format("YYYY-MM-DD")} for ${from_date.date} ${from_ampm}.`;
        numberofday_type = "H";
      } else if (number_of_days === "1.0") {
        day_description = `${number_of_days} day leave applied on ${moment(created).format("YYYY-MM-DD")} for ${from_date.date}.`;
        numberofday_type = "1";
      }
    
      const entity = {
        ...application,
        description: day_description,
        numberofday_type: { id: numberofday_type },
        current_balance: showBalance,
      };

      const appendTime = (date, ampm) => {
        return ampm === "AM" ? `${date} 08:00:00` : `${date} 13:00:00`;
      };

      const fromDateString = appendTime(moment(from_date.date).format("YYYY-MM-DD"), from_ampm);
      const toDateString = appendTime(moment(to_date.date).format("YYYY-MM-DD"), to_ampm);
    
      // **Check for Overlapping Leave First**
      dispatch(LeaveApplicationOverlap({edatetime: toDateString, employee: { id: userId}, sdatetime: fromDateString}))
        .unwrap()
        .then((overlapResponse) => {
          if (overlapResponse === "0.0") {
            // **No overlap, proceed with application**
            dispatch(postLeaveApplication(entity))
              .unwrap()
              .then((response) => {
                const applicationId = response.id;
    
                // Add sick or special attachment based on leave type
                if (leave_type.id === "SI") {
                  const entity2 = { ...cert, leave_application: { id: applicationId } };
                  dispatch(addMedicalCertificate(entity2));
                } else if (leave_type.id === "SP") {
                  if (cert && Object.keys(cert).length > 0) {
                    const entity3 = { ...cert, leave_application: { id: applicationId } };
                    dispatch(addSpecialAttachment(entity3));
                  }
                }
    
                // Send success log to Logstash
                // sendLogToLogstash({
                //   level: "info",
                //   message: "Leave application created successfully",
                //   timestamp: new Date().toISOString(),
                //   app: "LeaveApplicationSystem",
                //   data: { ...entity },
                // });
    
                Swal.fire("Leave Application Created", "", "success", "OK").then(() => {  
                  dispatch(resetForm());
                  navigate("/LeaveApplicationList");
                });
              })
              .catch((err) => {
                // Send error log to Logstash
                // sendLogToLogstash({
                //   level: "error",
                //   message: "Failed to create leave application",
                //   timestamp: new Date().toISOString(),
                //   app: "LeaveApplicationSystem",
                //   error: err.response?.data || err.message,
                // });
    
                Swal.fire("Error / Masalah", err.response?.data || "Something went wrong!", "error", "OK");
              });
          } else {
            // **Overlap detected, show warning**
            Swal.fire("", "This leave application OVERLAPS with one or existing leave applications.", "warning", "OK");
          }
        })
        .catch((err) => {
          // Send error log to Logstash
          // sendLogToLogstash({
          //   level: "error",
          //   message: "Failed to check leave application overlap",
          //   timestamp: new Date().toISOString(),
          //   app: "LeaveApplicationSystem",
          //   error: err.response?.data || err.message,
          // });
    
          Swal.fire("Error / Masalah", err.response?.data || "Something went wrong!", "error", "OK");
        });
    } catch (err) {
      // Send error log to Logstash
      // sendLogToLogstash({
      //   level: "error",
      //   message: "Unexpected error occurred in handleBtnSubmit",
      //   timestamp: new Date().toISOString(),
      //   app: "LeaveApplicationSystem",
      //   error: err.message,
      // });
    
      Swal.fire("Error / Masalah", err.message, "error", "OK");
    }    
  };

  const handleBtnEdit = () => {

    if (hasInvalidNumberOfDays()) {
      return;
    }

    // check attachment based on leave type
    if (leave_type.id === "SI") {
      if (!cert || Object.keys(cert).length === 0) {
        Swal.fire({
          text: "Please attach a medical certificate. / Sila lampirkan sijil sakit.",
          icon: "warning",
          confirmButtonText: "OK"
        });
        return; // Stop submission
      }
    }

    // Only apply max day check for Special Leave (SP)
    if (leave_type?.id === "SP") {
      const selectedSpecialLeaveType = SpecialLeaveTypeOption.find(
        (item) => item.id === application?.special_leave_type?.id
      );

      const daysRequested = parseFloat(number_of_days);
      const maxDays = selectedSpecialLeaveType?.max_days;

      if (daysRequested > maxDays) {
        setIsError(true);
        Swal.fire({
          text: `You can only apply up to ${maxDays} day(s) for this special leave type. / Anda hanya boleh memohon ${maxDays} hari untuk jenis cuti ini.`,
          icon: "warning",
          confirmButtonText: "OK"
        });
        return;
      }
    }

    if (
      !reason_for_application.trim() || 
      reason_for_application.trim().length < 3 || 
      !Object.values(application).includes(reason_for_application)
    ) {
      setReasonEmpty(true); // Highlight reason field
    
      Swal.fire({
        icon: 'warning',
        text: 'Please enter a reason with at least 3 characters. / Sila masukkan sebab dengan sekurang-kurangnya 3 huruf.',
      });
    
      return; // Stop further execution
    } else {
      setReasonEmpty(false);
    }

    let balanceAfterChange;
    let ori_balance = 0;

    const isZeroBalanceType = ["O", "UP"].includes(leave_type.id); 

    if (!id) {
      // New leave
      balanceAfterChange = Number(showBalance) - Number(number_of_days);
    } 
    else if (originalLeaveTypeId === leave_type.id) {
      // Editing existing leave
      balanceAfterChange = Number(showBalance) + Number(originalDaysApplied) - Number(number_of_days);

      if (!isZeroBalanceType) {
        ori_balance = Number(showBalance) + Number(originalDaysApplied);
      }
    }
    else if (originalLeaveTypeId !== leave_type.id) {
      // Changing leave type
      balanceAfterChange = Number(showBalance) - Number(number_of_days);

      if (!isZeroBalanceType) {
        ori_balance = Number(showBalance);
      }
    }

    // Threshold: Annual leave = -5, others = 0
    let minBalance = 0;

    if (leave_type?.id === "A") {
      minBalance = -5;
    } else if (leave_type?.id === "O" || leave_type?.id === "UP") {
      minBalance = null; // 👈 no limit
    } else {
      minBalance = 0;
    }

    // if (leave_type?.id === "O" || leave_type?.id === "UP") {
    //   minBalance = null;
    // } else {
    //   minBalance = 0;
    // }

    console.log(balanceAfterChange);

    // Only check balance if there is a limit
    if (minBalance !== null && balanceAfterChange < minBalance) {
      setIsError(true);
      console.log(showBalance, number_of_days, balanceAfterChange);
      Swal.fire({
        title: "Leave Balance Insufficient!",
        text: "You do not have enough leave balance. / Anda tidak mempunyai baki cuti yang cukup.",
        icon: "warning",
        button: "OK",
      });
      return;
    }

    // const minBalance = leave_type?.id === "A" ? -5 : 0;

    // if (balanceAfterChange < minBalance) {
    //   setIsError(true);
    //   console.log("Balance after change:", balanceAfterChange);
    //   Swal.fire({
    //     title: "Leave Balance Insufficient!",
    //     text: "You do not have enough leave balance. / Anda tidak mempunyai baki cuti yang cukup.",
    //     icon: "warning",
    //     button: "OK",
    //   });
    //   return;
    // }

    // if (leave_type?.id === "A") {
    //   let balanceAfterChange;
    
    //   if (!id) {
    //     // New leave
    //     balanceAfterChange = Number(showBalance) - Number(number_of_days);
    //   } else {
    //     // Editing existing leave
    //     balanceAfterChange = Number(showBalance) + Number(originalDaysApplied) - Number(number_of_days);
    //   }

    //   console.log(Number(showBalance), Number(originalDaysApplied), Number(number_of_days));
    
    //   if (balanceAfterChange < -5) {
    //     setIsError(true);
    //     console.log("Balance after change:", balanceAfterChange);
    //     Swal.fire({
    //       title: "Leave Balance Insufficient!",
    //       text: "You do not have enough leave balance.",
    //       icon: "warning",
    //       button: "OK",
    //     });
    //     return;
    //   }
    // }
    // else {
    //   let balanceAfterChange;
    
    //   if (!id) {
    //     // New leave
    //     balanceAfterChange = Number(showBalance) - Number(number_of_days);
    //   } else {
    //     // Editing existing leave
    //     balanceAfterChange = Number(showBalance) + Number(originalDaysApplied) - Number(number_of_days);
    //   }

    //   console.log(Number(showBalance), Number(originalDaysApplied), Number(number_of_days));
    
    //   if (balanceAfterChange < 0) {
    //     setIsError(true);
    //     console.log("Balance after change:", balanceAfterChange);
    //     Swal.fire({
    //       title: "Leave Balance Insufficient!",
    //       text: "You do not have enough leave balance.",
    //       icon: "warning",
    //       button: "OK",
    //     });
    //     return;
    //   }
    // }

    let day_description;
    // let toDate=to_date.date;
    let numberofday_type;

    if (number_of_days !== "1.0" && number_of_days !== "0.5") {
      day_description = `${number_of_days} days leave applied on ${moment(created).format("YYYY-MM-DD")}, from ${moment(from_date.date).format("YYYY-MM-DD")} ${from_ampm} to ${moment(to_date.date).format("YYYY-MM-DD")} ${to_ampm}.`;
      numberofday_type = "M";
    } else if (number_of_days === "0.5") {
      day_description = `${number_of_days} day leave applied on ${moment(created).format("YYYY-MM-DD")} for ${moment(from_date.date).format("YYYY-MM-DD")} ${from_ampm}.`;
      numberofday_type = "H";
    } else if (number_of_days === "1.0") {
      day_description = `${number_of_days} day leave applied on ${moment(created).format("YYYY-MM-DD")} for ${moment(from_date.date).format("YYYY-MM-DD")}.`;
      numberofday_type = "1";
    }

    const entity = {
      ...application,
      description: day_description,
      numberofday_type: { id: numberofday_type },
      current_balance: ori_balance
    };

    // Edit
    dispatch(editLeaveApplication(entity))
    .unwrap()
    .then(() => {
      if (leave_type.id === "SI" && cert.id) {
        dispatch(editMedicalCertificate({ id: cert.id, ...cert }));
      } else if (leave_type.id === "SI" && !cert.id) {
        const entity2 = { ...cert, leave_application: { id: entity.id } };
        dispatch(addMedicalCertificate(entity2));
      } else if (leave_type.id === "SP" && cert.id) {
        if (cert && Object.keys(cert).length > 0) {
          dispatch(editSpecialAttachment({ id: cert.id, ...cert }));
        }
      } else if (leave_type.id === "SP" && !cert.id) {
        if (cert && Object.keys(cert).length > 0) {
          const entity2 = { ...cert, leave_application: { id: entity.id } };
          dispatch(addSpecialAttachment(entity2));
        }
      }
      Swal.fire("Record Updated", "", "success", "OK").then(() => {
        navigate(`/LeaveApplicationList`);
      });
    })
    .catch((err) => {
      Swal.fire("", "This leave application OVERLAPS with one or existing leave applications.", "warning", "OK");
    });
  };

  const handleAttachmentChange = (event) => {
    const selectedFile = event.target.files[0];

    if (selectedFile) {
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];

      if (!allowedTypes.includes(selectedFile.type)) {
        Swal.fire({
          text: "Only JPG, PNG, or PDF files are allowed. / Hanya fail JPG, PNG atau PDF dibenarkan.",
          icon: "warning",
          confirmButtonText: "OK"
        });
        // Clear the file input
        event.target.value = null;

        const entity = { ...cert,
          file: "",
          filename: "",
          filetype: "",
          filesize: ""
        };
        dispatch(updateCert(entity));

        return;
      }

      const reader = new FileReader();
  
      reader.onloadend =  async () => {
        const fileResult = reader.result;
        let base64String;
  
        if (selectedFile.type === 'application/pdf') {
          // For PDF files, strip off the data URL prefix
          base64String = fileResult.split(',')[1];
        } else {
          // For images or other file types, use the full data URL
          setFullImage(fileResult);
          const editor = await openEditor({ src: selectedFile });
          base64String = editor?.editedImage?.getDataURL();
        }
  
        const entity = { ...cert,
          file: base64String,
          filename: selectedFile.name,
          filetype: selectedFile.type,
          filesize: selectedFile.size
        };
        console.log(entity);
        dispatch(updateCert(entity));
  
        // Log file details
        console.log("Base64 File:", base64String);
        console.log("Filename:", selectedFile.name);
        console.log("Filetype:", selectedFile.type);
        console.log("Filesize:", selectedFile.size, "bytes");
      };
  
      // Read the file as a data URL
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleEditImage = async () => {
    if (fullImage) {
      try {
        const editor = await openEditor({ src: fullImage });

        if (editor && editor.editedImage) {
          const updatedImage = editor.editedImage.getDataURL();
          const entity = { ...cert, file: updatedImage };
          dispatch(updateCert(entity));
        } else {
          console.error("Edited image not found.");
        }
      } catch (error) {
        console.error("Error opening editor:", error);
      }
    } else {
      console.error("Full image is not set.");
    }
  };

  return (
    <Container className="ContainerContent">
      {loading && (
        <ThreeDots
          visible={loading}
          height="80"
          width="80"
          color="#4fa94d"
          radius="9"
          ariaLabel="three-dots-loading"
          wrapperStyle={{
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: '9999',
          }}
        />
      )}
      <Card className="CardContent2">
        <h1 className="TitleHeader">Leave Application</h1>
        <Row className="my-3">
          <Col md={8} sm={8}>
            <Form.Label className="FormLabel fw-normal fs-5">Leave Type</Form.Label>
          </Col>
          <Col md={4} sm={4}>
            {(!id || application?.status?.description === "Pending Approval") ? (
              <Select
                name="leave_type"
                options={getLeaveOptions(balance)}
                value={getLeaveOptions(balance).find(
                  (option) => option.id === leave_type.id
                )}
                onChange={handleChangeDropdownLeaveType}

                /* 👇 avoid covered by the PDF viewer */
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            ) : (
              <Form.Control value={application?.leave_type?.description} readOnly disabled/>
            )}
            
          </Col>
        </Row>

        {/* Leave Available */}
        {(
          (!id && leave_type?.id !== "O" && leave_type?.id !== "UP" && leave_type?.id !== "AD") ||
          (id && leave_type?.id !== "O" && leave_type?.id !== "UP" && leave_type?.id !== "AD" && application?.status?.description === "Pending Approval")
        ) && (
          <Row className="my-2">
            <Col md={8} sm={8}>
              <Form.Label className="FormLabel fw-normal fs-5">Leave Available</Form.Label>
            </Col>
            <Col md={4} sm={4}>
              <Form.Control value={showBalance} readOnly disabled/>
            </Col>
          </Row>
        )}

        {/* Leave Advanced */}
        {leave_type?.id == "AD" && (
          <Row className="my-2">
            <Col md={8} sm={8}>
              <Form.Label className="FormLabel fw-normal fs-5">Leave Advanced</Form.Label>
            </Col>
            <Col md={4} sm={4}>
              <Form.Control readOnly disabled value={showBalance} />
            </Col>
          </Row>
        )}

        {leave_type?.id == "SI" && (
          <Row className="my-2">
            <Col md={8} sm={8}>
              <Form.Label className="FormLabel fw-normal fs-5">Medical Certificate</Form.Label>
            </Col>
            <Col md={4} sm={4}>
              <Form.Control type="file" onChange={handleAttachmentChange} disabled={!!id && application?.status?.id !== 100}/>
            </Col>
          </Row>
        )}

        {/* Special Leave */}
        {leave_type?.id === "SP" && (
          <>
            <Row className="my-2">
              <Col md={8} sm={8}>
                <Form.Label className="FormLabel fw-normal fs-5">
                  Special Leave Type
                </Form.Label>
              </Col>
              <Col md={4} sm={4}>
                {/* Case 1 & 3: allow selection (new OR existing with status = 100) */}
                {(!id || application?.status?.id === 100) ? (
                  <Select
                    name="special_leave_type"
                    options={filteredSpecialLeaveOptions}
                    // options={SpecialLeaveTypeOption}
                    onChange={handleChangeDropdownSpecialLeaveType}
                    value={
                      application?.special_leave_type
                        ? filteredSpecialLeaveOptions.find(
                            (option) => option.id === application.special_leave_type.id
                          )
                        : null
                    }

                    /* 👇 avoid covered by the PDF viewer */
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                  />
                ) : (
                  // Case 2: Existing + status !== 100 → show selected, disabled
                  <Form.Control
                    type="text"
                    value={application?.special_leave_type?.description || ""}
                    disabled
                  />
                )}
              </Col>
            </Row>
          </>
        )}

        {leave_type?.id == "SP" && (
          <Row className="my-2">
            <Col md={8} sm={8}>
              <Form.Label className="FormLabel fw-normal fs-5">Attachment</Form.Label>
            </Col>
            <Col md={4} sm={4}>
              <Form.Control type="file" onChange={handleAttachmentChange} disabled={!!id && application?.status?.id !== 100}/>
            </Col>
          </Row>
        )}

        {/* Conditional rendering based on attachment existence */}
        {cert && cert.file && (leave_type?.id === "SI" || leave_type?.id === "SP") && (
          <Row className="my-3">
            <Col
              md={12}
              sm={12}
              className="d-flex justify-content-center align-items-center"
            >
              {cert.filetype.startsWith("image/") ? (
                // 📷 Display image
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "10px",
                    backgroundColor: "#373c40",
                    height: "500px",
                    width: "100%",
                    overflow: "auto",
                  }}
                >
                  <Image
                    // src={cert.file}
                    src={
                      cert.file.startsWith("data:")
                        ? cert.file
                        : `data:${cert.filetype};base64,${cert.file}`
                    }
                    alt="Uploaded file"
                    fluid
                    style={{ maxWidth: "100%", height: "100%" }}
                    onClick={handleEditImage}
                  />
                </div>
              ) : (
                // 📄 Display PDF using PDFViewer (mobile-friendly)
                cert.file ? (
                  <div style={{ width: "100%" }}>
                    <PDFViewer base64Data={cert.file} height="500px" width="100%" />
                  </div>
                ) : (
                  <p>No PDF available</p>
                )
              )}
            </Col>
          </Row>
        )}

        <Row className="my-3">
          <Col md={6} sm={4}>
            <Form.Label className="FormLabel fw-normal fs-5">From</Form.Label>
          </Col>
          
          <Col md={2} sm={4}>
            {leave_type?.id !== "H" && (
              <div>
                <Form.Check
                  type="radio"
                  label="AM"
                  name="from_ampm"
                  value="AM"
                  style={{ color: "#fff" }}
                  checked={from_ampm === "AM"}
                  onChange={handleChangeForm}
                  disabled={!!id && application?.status?.id !== 100}
                />
                <Form.Check
                  type="radio"
                  label="PM"
                  name="from_ampm"
                  value="PM"
                  style={{ color: "#fff" }}
                  checked={from_ampm === "PM"}
                  onChange={handleChangeForm}
                  disabled={!!id && application?.status?.id !== 100}
                />
              </div>
            )}
          </Col>
          
          <Col md={4} sm={4}>
            {/* <Form.Control
              type="date"
              name="from_date"
              value={moment(from_date?.date).format("YYYY-MM-DD")}
              onChange={handleChangeFromDate}
              style={{
                border: isError ? "3px solid red" : "",
                borderRadius: "5px"
              }}
              disabled={!!id && application?.status?.id !== 100}
            /> */}
            <DatePicker
              selected={from_date?.date ? new Date(from_date?.date) : null}
              onChange={(date) =>
                handleChangeFromDate({
                  target: {
                    name: "from_date",
                    value: moment(date).format("YYYY-MM-DD"),
                  },
                })
              }
              dateFormat="yyyy-MM-dd"
              className={`form-control w-100 datepicker-icon ${isError ? 'error-border' : ''}`}
              disabled={!!id && application?.status?.id !== 100}
            />
          </Col>
        </Row>

        <Row className="my-2">
          <Col md={6} sm={4}>
            <Form.Label className="FormLabel fw-normal fs-5">To</Form.Label>
          </Col>
          <Col md={2} sm={4}>
            {leave_type?.id !== "H" && (
              <div>
                <Form.Check
                  type="radio"
                  label="AM"
                  name="to_ampm"
                  value="AM"
                  style={{ color: "#fff" }}
                  checked={to_ampm === "AM"}
                  onChange={handleChangeForm}
                  disabled={!!id && application?.status?.id !== 100}
                />
                <Form.Check
                  type="radio"
                  label="PM"
                  name="to_ampm"
                  value="PM"
                  style={{ color: "#fff" }}
                  checked={to_ampm === "PM"}
                  onChange={handleChangeForm}
                  disabled={!!id && application?.status?.id !== 100}
                />
              </div>
            )}
          </Col>
          <Col md={4} sm={4}>
            {/* <Form.Control
              type="date"
              name="to_date"
              value={moment(to_date?.date).format("YYYY-MM-DD")}
              onChange={handleChangeToDate}
              min={moment(from_date?.date).format("YYYY-MM-DD")}
              style={{
                border: isError ? "3px solid red" : "",
                borderRadius: "5px"
              }}
              disabled={!!id && application?.status?.id !== 100}
            /> */}
            <DatePicker
              selected={to_date?.date ? new Date(to_date?.date) : null}
              onChange={(date) =>
                handleChangeToDate({
                  target: {
                    name: "to_date",
                    value: moment(date).format("YYYY-MM-DD"),
                  },
                })
              }
              minDate={moment(from_date?.date).format("YYYY-MM-DD")}
              dateFormat="yyyy-MM-dd"
              className={`form-control w-100 datepicker-icon ${isError ? 'error-border' : ''}`}
              disabled={!!id && application?.status?.id !== 100}
            />
          </Col>
        </Row>

        <Row className="my-2">
          <Col md={8} sm={8}>
            <Form.Label className="FormLabel fw-normal fs-5">Location</Form.Label>
          </Col>
          <Col md={4} sm={4}>
            {(!id || (id && application?.status?.id === 100)) ? (
              <Select
                name="location_type"
                options={LocationOption}
                value={LocationOption.find(
                  (option) => option.id === location_type.id
                )} // Correctly set the value to match the selected option
                onChange={handleChangeDropdownLocation}
              />
            ) : (
              <Form.Control value={application?.location_type?.description} readOnly disabled/>
            )}
          </Col>
        </Row>

        <Row className="my-3">
          <Col md={8} sm={8}>
            <Form.Label className="FormLabel fw-normal fs-5">No. Days Applying</Form.Label>
          </Col>
          <Col md={4} sm={4}>
            <Form.Control readOnly disabled value={number_of_days} />
          </Col>
        </Row>

        {id && (
          <>
          <Row className="my-2">
            <Col md={8} sm={8}>
              <Form.Label className="FormLabel fw-normal fs-5">Applied On</Form.Label>
            </Col>
            <Col md={4} sm={4}>
              <Form.Control readOnly disabled value={moment(application.created).format("YYYY-MM-DD")} />
            </Col>
          </Row>

          <Row className="my-2">
            <Col md={8} sm={8}>
              <Form.Label className="FormLabel fw-normal fs-5">Status</Form.Label>
            </Col>
            <Col md={4} sm={4}>
              <Form.Control readOnly disabled value={status?.description} />
            </Col>
          </Row>

          <Row className="my-2">
            <Col md={12} sm={12}>
              <Form.Label className="FormLabel fw-normal fs-5">Description</Form.Label>
              <Form.Control as="textarea" value={description} readOnly disabled />
            </Col>
          </Row>
          </>
        )}

        <Row className="my-2">
          <Col md={12} sm={12}>
            <Form.Label className="FormLabel fw-normal fs-5">Reason</Form.Label>
            <Form.Control 
              as="textarea"
              name="reason_for_application"
              value={reason_for_application}
              onChange={handleChangeForm}
              style={{
                border: isReasonEmpty ? "3px solid red" : "",
                borderRadius: "5px"
              }}
              disabled={!!id && application?.status?.id !== 100}
            />
          </Col>
        </Row>

        {id && application?.status?.description !== "Pending Approval" && (
          <>
          <Row className="my-2">
            <Col md={12} sm={12}>
              <Form.Label className="FormLabel fw-normal fs-5">Supervisor Remark</Form.Label>
              <Form.Control as="textarea" value={application.remark} readOnly disabled />
            </Col>
          </Row>

          <Row className="my-2">
            <Col md={12} sm={12}>
              <Form.Label className="FormLabel fw-normal fs-5">HR Remark</Form.Label>
              <Form.Control as="textarea" value={application.hr_remark} readOnly disabled />
            </Col>
          </Row>
          <hr style={{ borderTop: '2px solid white' }}/>
          {(application?.status?.description === "Leave Withdrawn by staff (Pending Approval)" || application?.status?.description === "Leave Withdrawn by staff with Approval") && (
            <Row className="my-2">
              <Col md={12} sm={12}>
                <Form.Label className="FormLabel fw-normal fs-5">HR Remark for Withdrawal</Form.Label>
                <Form.Control as="textarea" value={applicationDetails?.remark} readOnly disabled/>
              </Col>
            </Row>
          )}
          {application?.status?.description === "Approved by Supervisor" && (
            <Row className="my-2">
            <Col md={12} sm={12}>
              <Form.Label className="FormLabel fw-normal fs-5">Reason for Withdrawal</Form.Label>
              <Form.Control as="textarea" name="reason_withdrawal" value={reason_withdrawal} onChange={handleReasonWithdrawal}/>
            </Col>
          </Row>
          )}
          </>
        )}

        <div className="d-flex justify-content-between">
          <ButtonGroup className="my-2">
            <Button className="Button" onClick={handleBtnBack}>
              Back
            </Button>
          </ButtonGroup>
          <ButtonGroup className="my-2">
            {id && application?.status?.description === "Pending Approval" && (
              <>
              <Button className="Button" onClick={handleBtnDelete}>
                Delete
              </Button>
              &nbsp; &nbsp;
              </>
            )}

            {id && application?.status?.description === "Approved by Supervisor" && (
              <>
              <Button className="Button" onClick={handleBtnWithdraw}>
                Withdraw
              </Button>
              &nbsp; &nbsp;
              </>
            )}

            {id && application?.status?.description !== "Pending Approval" && (
              <Button className="Button" disabled>
                Submit
              </Button>
            )}
            {id && application?.status?.description === "Pending Approval" && (
              <Button className="Button" onClick={handleBtnEdit}>
                Update
              </Button>
            )}
            {!id && (
              <Button className="Button" onClick={handleBtnSubmit}>
                Submit
              </Button>
            )}
          </ButtonGroup>
        </div>
      </Card>
    </Container>
  );
};
export default LeaveApplicationForm;